if (!customElements.get('cta-carousel')) {
  class CtaCarousel extends HTMLElement {
    connectedCallback() {
      this.swiperEl = this.querySelector('[data-cta-carousel-swiper]');
      this.controlsEl = this.querySelector('[data-cta-carousel-controls]');
      this.prevEl = this.querySelector('[data-cta-carousel-prev]');
      this.nextEl = this.querySelector('[data-cta-carousel-next]');
      this.progressFillEl = this.querySelector('[data-cta-carousel-progress-fill]');
      this.columns = parseInt(this.dataset.columns, 10) || 3;
      this.blockCount = parseInt(this.dataset.blockCount, 10) || 0;
      this.mobileLayout = this.dataset.mobileLayout || 'carousel';
      this.swiper = null;
      this.swiperRetryTimer = null;
      this.mqlDesktop = window.matchMedia('(min-width: 1025px)');
      this.mqlTablet = window.matchMedia('(min-width: 768px)');
      this.onViewportChange = this.onViewportChange.bind(this);

      this.bindViewportListener(this.mqlDesktop);
      this.bindViewportListener(this.mqlTablet);

      this.setup();
    }

    bindViewportListener(mql) {
      if (mql.addEventListener) {
        mql.addEventListener('change', this.onViewportChange);
      } else if (mql.addListener) {
        mql.addListener(this.onViewportChange);
      }
    }

    unbindViewportListener(mql) {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', this.onViewportChange);
      } else if (mql.removeListener) {
        mql.removeListener(this.onViewportChange);
      }
    }

    disconnectedCallback() {
      this.clearSwiperRetry();
      this.unbindViewportListener(this.mqlDesktop);
      this.unbindViewportListener(this.mqlTablet);
      this.destroySwiper();
    }

    getViewport() {
      if (this.mqlDesktop.matches) return 'desktop';
      if (this.mqlTablet.matches) return 'tablet';
      return 'mobile';
    }

    clearSwiperRetry() {
      if (this.swiperRetryTimer) {
        clearTimeout(this.swiperRetryTimer);
        this.swiperRetryTimer = null;
      }
    }

    fallbackToStatic() {
      this.clearSwiperRetry();
      this.classList.add('is-static');
      this.classList.remove('is-carousel');
      this.destroySwiper();
      if (this.controlsEl) this.controlsEl.hidden = true;
    }

    onViewportChange() {
      this.setup();
    }

    shouldUseCarousel(viewport) {
      if (viewport === 'desktop') {
        return this.blockCount > this.columns;
      }
      if (viewport === 'tablet') {
        return this.blockCount > 2;
      }
      return this.mobileLayout === 'carousel' && this.blockCount > 1;
    }

    setup() {
      const viewport = this.getViewport();
      const useCarousel = this.shouldUseCarousel(viewport);

      this.classList.toggle('is-static', !useCarousel);
      this.classList.toggle('is-carousel', useCarousel);

      if (useCarousel) {
        if (!this.swiper) this.initSwiper();
      } else {
        this.destroySwiper();
        if (this.controlsEl) this.controlsEl.hidden = true;
      }
    }

    updateProgress(swiper) {
      if (!this.progressFillEl || !swiper) return;

      if (swiper.isLocked) {
        this.progressFillEl.style.width = '100%';
        return;
      }

      const slidesPerView = Number(swiper.params.slidesPerView) || 1;
      const total = swiper.slides.length || 1;
      const visibleRatio = Math.min(slidesPerView / total, 1);
      const progress = Math.min(Math.max(swiper.progress, 0), 1);
      const fill = visibleRatio + (1 - visibleRatio) * progress;
      this.progressFillEl.style.width = `${fill * 100}%`;
    }

    getSwiperOptions() {
      const self = this;

      return {
        slidesPerView: 1.15,
        spaceBetween: 16,
        breakpoints: {
          768: {
            slidesPerView: 2,
            spaceBetween: 24,
          },
          1025: {
            slidesPerView: 2,
            spaceBetween: 24,
          },
        },
        watchOverflow: true,
        watchSlidesProgress: true,
        grabCursor: true,
        mousewheel: {
          forceToAxis: true,
          releaseOnEdges: true,
        },
        navigation: {
          prevEl: this.prevEl,
          nextEl: this.nextEl,
        },
        on: {
          init(swiper) {
            if (self.controlsEl) {
              self.controlsEl.hidden = swiper.isLocked;
            }
            self.updateProgress(swiper);
          },
          progress(swiper) {
            self.updateProgress(swiper);
          },
          slideChange(swiper) {
            self.updateProgress(swiper);
          },
          resize(swiper) {
            if (self.controlsEl) {
              self.controlsEl.hidden = swiper.isLocked;
            }
            self.updateProgress(swiper);
          },
        },
      };
    }

    initSwiper() {
      this.clearSwiperRetry();
      let attempts = 0;
      const maxAttempts = 50;

      const start = () => {
        if (!this.isConnected) return;

        if (typeof Swiper === 'undefined') {
          attempts += 1;
          if (attempts >= maxAttempts) {
            this.fallbackToStatic();
            return;
          }
          if (typeof addSwiperCssJs === 'function') addSwiperCssJs();
          this.swiperRetryTimer = setTimeout(start, 100);
          return;
        }

        this.clearSwiperRetry();
        this.destroySwiper();
        this.swiper = new Swiper(this.swiperEl, this.getSwiperOptions());
      };

      start();
    }

    destroySwiper() {
      this.clearSwiperRetry();
      if (this.swiper && typeof this.swiper.destroy === 'function') {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  customElements.define('cta-carousel', CtaCarousel);
}

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    const root = event.target.querySelector('cta-carousel');
    if (root && typeof root.setup === 'function') {
      root.setup();
    }
  });
}
